// Le marché fictif : un pas par jour pour les cours, un pas par mois pour les phases, les nouvelles et les dividendes.
// Il a son propre hasard, pour ne pas changer les autres tirages de la partie.
import { ASSETS, REGIMES, TRANSITIONS, getAsset } from '../content/market';
import { REAL_ESTATE } from '../content/property';
import { round2 } from '../engine/finance';
import { nextRandom, randomInt } from '../engine/rng';
import type { AssetId, GameState, MarketRegime, MarketState } from '../engine/state';
import { DAYS_PER_YEAR, dateAtDay, monthIndexAt } from '../engine/time';

const DT = 1 / DAYS_PER_YEAR;
/** La valeur de fond suit le cours, lissée sur environ deux ans. */
const EMA_RATE = 1 / (2 * DAYS_PER_YEAR);
/** Un prix ne descend jamais sous 50 centimes. */
const PRICE_FLOOR = 0.5;

/** Ce que le changement de mois apporte : les grosses nouvelles, et les dividendes par action. */
export interface MarketMonth {
  news: { asset: AssetId; up: boolean }[];
  dividends: Partial<Record<AssetId, number>>;
}

/** Un tirage selon la loi normale (méthode de Box et Muller), avec le hasard propre au marché. */
function normal(market: MarketState): number {
  const u1 = Math.max(1e-12, nextRandom(market));
  const u2 = nextRandom(market);
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

/**
 * La variation du jour d'un fonds à levier, en logarithme : le multiple de la variation du marché, pas de son
 * logarithme. C'est ce recalcul quotidien qui l'use quand le marché monte et descend sans avancer.
 */
export function leveragedMove(move: number, leverage: number): number {
  return Math.log(Math.max(0.01, 1 + leverage * (Math.exp(move) - 1)));
}

/** Un jour de bourse : le marché, puis chaque placement, bougent au hasard autour de leur tendance. */
export function marketDay(market: MarketState, day: number): void {
  const regime = REGIMES[market.regime];
  const move = (regime.drift - regime.vol ** 2 / 2) * DT + regime.vol * Math.sqrt(DT) * normal(market);
  market.index *= Math.exp(move);
  market.indexPeak = Math.max(market.indexPeak, market.index);
  market.indexEma += (market.index - market.indexEma) * EMA_RATE;
  const years = day / DAYS_PER_YEAR;
  for (const asset of ASSETS) {
    const marketMove = asset.leverage ? leveragedMove(move, asset.leverage) : asset.beta * move;
    let r = marketMove + (asset.drift - asset.vol ** 2 / 2 - asset.fees) * DT;
    if (asset.vol > 0) r += asset.vol * Math.sqrt(DT) * normal(market);
    if (asset.cycle) {
      const { amplitude, years: period } = asset.cycle;
      r += amplitude * ((2 * Math.PI) / period) * Math.cos((2 * Math.PI * years) / period) * DT;
    }
    market.prices[asset.id] = Math.max(PRICE_FLOOR, market.prices[asset.id] * Math.exp(r));
    market.ema[asset.id] += (market.prices[asset.id] - market.ema[asset.id]) * EMA_RATE;
  }
}

function setRegime(market: MarketState, regime: MarketRegime, months: number): void {
  market.regime = regime;
  market.regimeMonths = 0;
  market.monthsLeft = months;
}

/** Chaque mois, le marché change parfois de phase : croissance, bulle, krach, reprise. */
function nextRegime(market: MarketState): void {
  market.regimeMonths += 1;
  const crash = () => randomInt(market, TRANSITIONS.crashMonths[0], TRANSITIONS.crashMonths[1]);
  const roll = nextRandom(market);
  switch (market.regime) {
    case 'croissance':
      if (roll < TRANSITIONS.bubbleChance) setRegime(market, 'bulle', 0);
      else if (roll < TRANSITIONS.bubbleChance + TRANSITIONS.suddenCrashChance) setRegime(market, 'krach', crash());
      break;
    case 'bulle':
      if (roll < TRANSITIONS.burstChance || market.regimeMonths >= TRANSITIONS.maxBubbleMonths) {
        setRegime(market, 'krach', crash());
      }
      break;
    case 'reprise':
      market.monthsLeft -= 1;
      if (market.monthsLeft <= 0) setRegime(market, 'croissance', 0);
      break;
    default:
      // Après un krach ou la première baisse, le marché se reprend.
      market.monthsLeft -= 1;
      if (market.monthsLeft <= 0) {
        const [min, max] = market.regime === 'correction' ? [6, 12] : TRANSITIONS.recoveryMonths;
        setRegime(market, 'reprise', randomInt(market, min, max));
      }
  }
}

/** L'immobilier, chaque mois : une lente hausse, et de temps en temps quelques années de baisse. */
function realEstateMonth(market: MarketState): void {
  const { growth, slump, vol, slumpChance, slumpMonths } = REAL_ESTATE;
  if (market.realEstateSlump > 0) market.realEstateSlump -= 1;
  else if (nextRandom(market) < slumpChance) market.realEstateSlump = randomInt(market, slumpMonths[0], slumpMonths[1]);
  const drift = market.realEstateSlump > 0 ? slump : growth;
  const dt = 1 / 12;
  market.realEstate *= Math.exp((drift - vol ** 2 / 2) * dt + vol * Math.sqrt(dt) * normal(market));
  market.realEstateHistory.push(round2(market.realEstate));
}

/** Le changement de mois : la phase, la première baisse garantie, les nouvelles, les dividendes, l'historique. */
export function marketMonth(state: GameState, day: number): MarketMonth {
  const market = state.market;
  nextRegime(market);
  realEstateMonth(market);
  // La première baisse, un à deux ans après le premier achat : sauf si le marché baisse déjà.
  if (!market.firstDropDone && market.firstDropDay !== null && day >= market.firstDropDay) {
    market.firstDropDone = true;
    if (market.regime !== 'krach') setRegime(market, 'correction', TRANSITIONS.correctionMonths);
  }
  const result: MarketMonth = { news: [], dividends: {} };
  const calendarMonth = dateAtDay(state.startDate, day).getMonth();
  const may = calendarMonth === 4;
  if (calendarMonth === 0) {
    // Les fonds euros placent surtout en obligations : leur taux suit un peu l'année des obligations.
    const bonds = market.history.oblig;
    const past = bonds[Math.max(0, bonds.length - 12)];
    const change = past > 0 ? market.prices.oblig / past - 1 : 0.03;
    market.eurosRate = Math.round(Math.min(0.035, Math.max(0.015, 0.018 + 0.3 * change)) * 1000) / 1000;
  }
  for (const asset of ASSETS) {
    if (asset.news && nextRandom(market) < asset.news.chancePerMonth) {
      const up = nextRandom(market) < 0.5;
      const factor = up ? 1 + asset.news.up : 1 - asset.news.down;
      market.prices[asset.id] = Math.max(PRICE_FLOOR, market.prices[asset.id] * factor);
      // Un traitement autorisé apporte enfin des bénéfices ; un essai raté les fait disparaître.
      if (asset.lossMaking) market.profitable = { ...market.profitable, [asset.id]: up };
      result.news.push({ asset: asset.id, up });
    }
    if (asset.dividend && may) {
      // Le dividende suit les bénéfices (la valeur de fond) ; le jour du détachement, le cours baisse d'autant.
      const perShare = round2(Math.min(market.ema[asset.id] * asset.dividend, market.prices[asset.id] / 2));
      market.prices[asset.id] = Math.max(PRICE_FLOOR, market.prices[asset.id] - perShare);
      market.ema[asset.id] = Math.max(PRICE_FLOOR, market.ema[asset.id] - perShare);
      result.dividends[asset.id] = perShare;
    }
    market.history[asset.id].push(round2(market.prices[asset.id]));
    market.emaHistory[asset.id].push(round2(market.ema[asset.id]));
  }
  market.indexHistory.push(round2(market.index));
  return result;
}

/** Fait avancer la bourse jusqu'au jour de jeu en cours : un pas par jour entier, et un pas par mois. */
export function advanceMarket(state: GameState): MarketMonth[] {
  const market = state.market;
  const today = Math.floor(state.day);
  const months: MarketMonth[] = [];
  while (market.lastDay < today) {
    market.lastDay += 1;
    marketDay(market, market.lastDay);
    const month = monthIndexAt(state.startDate, market.lastDay);
    if (month > market.lastMonth) {
      market.lastMonth = month;
      months.push(marketMonth(state, market.lastDay));
    }
  }
  return months;
}

/** Le prix d'une part aujourd'hui. */
export function priceOf(state: GameState, asset: AssetId): number {
  return state.market.prices[asset];
}

/** La variation d'un placement sur les derniers mois (0,1 pour +10 %), d'après l'historique mensuel. */
export function changeOver(state: GameState, asset: AssetId, months: number): number {
  const history = state.market.history[asset];
  const past = history[Math.max(0, history.length - 1 - months)];
  return past > 0 ? state.market.prices[asset] / past - 1 : 0;
}

/** Le PER : le prix divisé par le bénéfice annuel ; pour un fonds d'actions, le PER moyen du marché ; null en pertes. */
export function assetPer(state: GameState, id: AssetId): number | null {
  const asset = getAsset(id);
  const market = state.market;
  if (!asset.per || (asset.lossMaking && !market.profitable[id])) return null;
  if (asset.kind === 'fonds') return (asset.per * market.index) / market.indexEma;
  return (asset.per * market.prices[id]) / market.ema[id];
}

/** Le rendement du dividende : le dividende de l'année (qui suit les bénéfices) rapporté au prix du jour. */
export function dividendYield(state: GameState, id: AssetId): number {
  const asset = getAsset(id);
  if (!asset.dividend) return 0;
  return (asset.dividend * state.market.ema[id]) / state.market.prices[id];
}

/** La croissance des bénéfices sur un an ; null tant qu'il n'y a pas de bénéfices ou pas un an d'historique. */
export function earningsGrowth(state: GameState, id: AssetId): number | null {
  const asset = getAsset(id);
  if (asset.lossMaking && !state.market.profitable[id]) return null;
  const history = state.market.emaHistory[id];
  if (history.length < 13) return null;
  return state.market.ema[id] / history[history.length - 12] - 1;
}

/** L'endettement : il monte quand l'entreprise vaut moins que sa valeur de fond (ses fonds propres fondent). */
export function debtRatio(state: GameState, id: AssetId): number {
  const asset = getAsset(id);
  const pressure = Math.min(1.6, Math.max(0.7, state.market.ema[id] / state.market.prices[id]));
  return (asset.debt ?? 0) * pressure;
}

/** La volatilité sur un an, d'après les douze derniers cours de fin de mois ; null sans un an d'historique. */
export function volatility(state: GameState, id: AssetId): number | null {
  const history = state.market.history[id];
  if (history.length < 13) return null;
  const recent = history.slice(-13);
  const returns = recent.slice(1).map((price, index) => Math.log(price / recent[index]));
  const mean = returns.reduce((sum, value) => sum + value, 0) / returns.length;
  const variance = returns.reduce((sum, value) => sum + (value - mean) ** 2, 0) / (returns.length - 1);
  return Math.sqrt(variance * 12);
}

/** Le rendement actuariel d'un fonds d'obligations : ce que ses obligations rapportent par an, frais déduits. */
export function bondYield(id: AssetId): number {
  const asset = getAsset(id);
  return asset.drift - asset.fees;
}

/** Programme la première baisse, au premier achat : un à deux ans plus tard. */
export function scheduleFirstDrop(state: GameState): void {
  const market = state.market;
  if (market.firstDropDay !== null) return;
  const [min, max] = TRANSITIONS.firstDropDays;
  market.firstDropDay = Math.floor(state.day) + randomInt(market, min, max);
}
